import { getItemWithExpiration } from '@/lib/utils';
import { FacilityTransferBody } from './types';

/*
 * 결과를 모르는 양도 요청(망 끊김·5xx·503 이어 완료 필요)의 요청 키와 본문을 브라우저 localStorage 에 남긴다.
 *
 * 요청 키는 창 안의 ref 에만 있으면 창·탭을 닫는 순간 사라진다. 그 뒤 다시 열어 같은 내용을 새 키로 보내면,
 * 서버가 첫 요청을 이미 처리했을 때 같은 기간이 두 번 넘어간다. 그래서 보내기 직전에 저장하고(저장에 실패하면
 * 보내지 않는다), 서버가 확정 응답을 줄 때만 지운다. 같은 브라우저에서는 탭·창을 닫거나 다시 로그인해도
 * 남는다. 다른 브라우저·다른 기기에는 없다 — 그때는 서버 이력의 「마무리 필요」·「이어서 완료」가 대신한다.
 *
 * 시간이 지났다고 지우지 않는다. 서버가 이미 처리한 요청은 「마무리 필요」에도 안 잡히므로, 오래된 기록을
 * 지우면 다음 날 같은 내용을 새 키로 다시 보내 이중 양도가 된다.
 *
 * 키에 로그인 계정과 센터를 넣는다 — 같은 브라우저를 다른 계정·다른 센터가 써도 남의 요청이 보이지 않게.
 * 새 자물쇠 비밀번호는 저장하지 않는다(서버는 비밀번호를 실어 보냈을 때만 재전송 비교에 쓴다).
 */

const STORAGE_PREFIX = 'rappo:facilityTransfer:pending:';
/** 이전 판(sessionStorage, 계정 구분 없음)의 키. 읽을 때 한 번 옮긴다. */
const LEGACY_SESSION_PREFIX = 'rappo:facilityTransfer:pending:';

export type FacilityTransferPendingRecord = {
    /** 저장 본문 — newLockNumber 는 빠져 있다 */
    body: FacilityTransferBody;
    savedAt: number;
    /** 다시 열었을 때 무엇을 보냈는지 보여 주기 위한 요약(서버 미리보기 값 그대로) */
    summary: {
        recipientName: string;
        productName: string;
        days: number;
        isFull: boolean;
        targetStartDate: string | null;
        targetEndDate: string | null;
    };
};

const accountScope = (): string => {
    let authObject: any = null;
    try {
        authObject = getItemWithExpiration('authObject');
    } catch {
        authObject = null;
    }
    const account = String(authObject?.adminId || authObject?.id || authObject?.superAdminId || 'anon');
    const center = String(authObject?.centerId || 'nocenter');
    return `${account}:${center}`;
};

/** 저장 키(테스트·진단용으로 내보낸다) */
export const pendingStorageKey = (subscriptionId: string) => `${STORAGE_PREFIX}${accountScope()}:${subscriptionId}`;
const legacyKey = (subscriptionId: string) => `${LEGACY_SESSION_PREFIX}${subscriptionId}`;

const withoutPassword = (body: FacilityTransferBody): FacilityTransferBody => {
    const { newLockNumber: _omit, ...rest } = body;
    return rest;
};

const isValidRecord = (value: any): value is FacilityTransferPendingRecord =>
    !!value &&
    typeof value === 'object' &&
    typeof value.savedAt === 'number' &&
    !!value.body &&
    typeof value.body.clientOperationId === 'string' &&
    typeof value.body.toMemberId === 'string' &&
    typeof value.body.startDate === 'string' &&
    typeof value.body.days === 'number' &&
    typeof value.body.moveLocker === 'boolean' &&
    !!value.summary;

/**
 * 보내기 직전 저장. 성공하면 true. 실패(예외·용량 초과·저장소 없음)면 false — 호출부는 **보내지 않는다**.
 */
export const savePendingFacilityTransfer = (
    subscriptionId: string,
    record: Omit<FacilityTransferPendingRecord, 'savedAt'>,
    now = Date.now(),
): boolean => {
    try {
        const storage = window.localStorage;
        const key = pendingStorageKey(subscriptionId);
        const value = JSON.stringify({ ...record, body: withoutPassword(record.body), savedAt: now });
        storage.setItem(key, value);
        // 실제로 남았는지 다시 읽어 확인한다(일부 환경은 예외 없이 저장을 버린다).
        return storage.getItem(key) === value;
    } catch {
        return false;
    }
};

export const clearPendingFacilityTransfer = (subscriptionId: string) => {
    try {
        window.localStorage.removeItem(pendingStorageKey(subscriptionId));
    } catch {
        /* noop */
    }
    try {
        window.sessionStorage.removeItem(legacyKey(subscriptionId));
    } catch {
        /* noop */
    }
};

export type PendingReadResult = { ok: true; record: FacilityTransferPendingRecord | null } | { ok: false };

/**
 * 저장된 미확정 요청을 읽는다. 저장소를 읽지 못하면 ok:false — 호출부는 새 양도를 막고 다시 읽기를 안내한다
 * (「없음」으로 보고 새 키를 만들면 이중 양도가 될 수 있다). 깨진 값만 지우고 null.
 * 이전 판의 sessionStorage 기록이 있으면 이번 계정·센터 키로 옮긴다.
 */
export const readPendingFacilityTransfer = (subscriptionId: string): PendingReadResult => {
    let storage: Storage;
    try {
        storage = window.localStorage;
        storage.getItem('__probe__');
    } catch {
        return { ok: false };
    }
    const key = pendingStorageKey(subscriptionId);
    try {
        let raw = storage.getItem(key);
        if (!raw) {
            let legacy: string | null = null;
            try {
                legacy = window.sessionStorage.getItem(legacyKey(subscriptionId));
            } catch {
                legacy = null;
            }
            if (legacy) {
                storage.setItem(key, legacy);
                if (storage.getItem(key) !== legacy) return { ok: false };
                try {
                    window.sessionStorage.removeItem(legacyKey(subscriptionId));
                } catch {
                    /* noop */
                }
                raw = legacy;
            }
        }
        if (!raw) return { ok: true, record: null };
        let parsed: any;
        try {
            parsed = JSON.parse(raw);
        } catch {
            parsed = null;
        }
        if (!isValidRecord(parsed)) {
            storage.removeItem(key);
            return { ok: true, record: null };
        }
        return { ok: true, record: { ...parsed, body: withoutPassword(parsed.body) } };
    } catch {
        return { ok: false };
    }
};

/** 저장된 미확정 요청(읽기 실패는 null). 진입 버튼 표시 등 가벼운 용도. */
export const loadPendingFacilityTransfer = (subscriptionId: string): FacilityTransferPendingRecord | null => {
    const result = readPendingFacilityTransfer(subscriptionId);
    return result.ok ? result.record : null;
};

/**
 * 결과를 모르는 양도 요청이 남아 있는가. 있으면 이용권 상태와 상관없이(전부 양도 중 원본이 만료로 바뀌었어도)
 * 「이어서 양도하기」 진입을 보여야 한다 — 신규 양도 자격과 기존 요청 마무리 자격은 다르다.
 */
export const hasPendingFacilityTransfer = (subscriptionId: string): boolean => !!loadPendingFacilityTransfer(subscriptionId);
